import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import headerImage from "./assets/vitrine/cocreation/header.png";
import section11 from "./assets/vitrine/cocreation/section1_1.png";
import section12 from "./assets/vitrine/cocreation/section1_2.png";
import section13 from "./assets/vitrine/cocreation/section1_3.png";
import section14 from "./assets/vitrine/cocreation/section1_4.png";
import sectionSlots from "./assets/vitrine/cocreation/section_slots.png";
import section4 from "./assets/vitrine/cocreation/section4.png";
import section51 from "./assets/vitrine/cocreation/section5_1.png";
import section52 from "./assets/vitrine/cocreation/section5_2.png";
import section7 from "./assets/vitrine/cocreation/section7.png";
import section81 from "./assets/vitrine/cocreation/section8_1.png";
import section82 from "./assets/vitrine/cocreation/section8_2.png";
import section10 from "./assets/vitrine/cocreation/section10.jpg";
import "./CoCreationPage.css";

const InternalAction = ({ to, children }) => (
  <Link className="coc-action" to={to}>{children}</Link>
);

const MediaGroup = ({ images, variant = "single" }) => (
  <div className={`coc-media coc-media-${variant}`}>
    {images.map((src, index) => (
      <img key={`${src}-${index}`} src={src} alt="" loading="lazy" />
    ))}
  </div>
);

const Section = ({
  title,
  subtitle,
  bullets,
  benefit,
  images = [],
  mediaVariant = "single",
  reverse = false,
  action,
  children,
}) => (
  <section className={`coc-section${reverse ? " is-reverse" : ""}${images.length === 0 ? " is-text-only" : ""}`}>
    <div className="coc-sectionInner">
      {images.length > 0 ? <MediaGroup images={images} variant={mediaVariant} /> : null}
      <div className="coc-copy">
        <h2>{title}</h2>
        <p className="coc-subtitle">{subtitle}</p>
        <div className="coc-detail">
          <strong>How friction is removed:</strong>
          {children || (
            <ul>
              {bullets.map((item) => <li key={item}>{item}</li>)}
            </ul>
          )}
        </div>
        <p className="coc-benefit"><strong>User benefit:</strong> {benefit}</p>
        {action ? <InternalAction to={action.to}>{action.label}</InternalAction> : null}
      </div>
    </div>
  </section>
);

export default function CoCreationPage() {
  useEffect(() => {
    document.title = "From instant co-creation to perpetual media: enterprise video reinvented.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="coc-page">
      <section className="coc-hero">
        <img className="coc-heroImage" src={headerImage} alt="" />
        <div className="coc-heroShade" />
        <div className="coc-heroContent">
          <h1>From instant co-creation to perpetual media: enterprise video reinvented.</h1>
          <p>Mobilize on-the-ground teams through AI-structured micro-missions, anchored to a single persistent URL where scenes hot-swap in place without re-rendering.</p>
        </div>
      </section>

      <Section
        title="AI-Guided Ideation (Instant AI Proposals in ~370 ms)"
        subtitle="Never face a blank page: let AI generate and suggest shoot-ready video concepts in milliseconds."
        images={[section11, section12, section13, section14]}
        mediaVariant="quad"
        bullets={[
          "Ultra-fast suggestions (~370 ms): Recommendation services deliver pre-computed video concepts with virtually zero waiting time.",
          '"Suggested Weaves" carousel: Curated cards appear right in the discovery stream featuring relevance tags such as "Top match" and affinity percentage indicators.',
          'Spontaneous narrative variants: Creators can instantly refresh and browse alternative storylines with a single tap on the "New ideas" action.',
        ]}
        benefit="Launching a project requires zero upfront writing or conceptual friction."
        action={{ to: "/use-cases/ai-proposal", label: "Explore AI recommendation →" }}
      />

      <Section
        title="The Weave Wizard: From Concept to Launch in 3 Simple Steps"
        subtitle="Turn a spark into a coordinated shoot in less than two minutes."
        bullets={[
          "Guided 3-phase flow: A linear, clean progression moving seamlessly through 1. Blueprint, 2. Slots, and 3. Settings.",
          "Instant visual covers via Google Imagen: When no media asset is uploaded, the platform generates a photorealistic cover image from project keywords in under 3.5 seconds.",
          "Ready-made narrative layouts: Choose from 6 specialized structural presets (Storyboard, Gallery, Seed, Symphony, Relay, Versus) to standardize pacing and contributions immediately.",
        ]}
        benefit="Framing a multi-contributor video is as fast and intuitive as drafting a standard social post."
        action={{ to: "/use-cases/wizard", label: "Explore the 3-step wizard →" }}
      />

      <Section
        title="Precision Slots: Turnkey Micro-Missions for On-the-Ground Creators"
        subtitle="Replace complex 10-page creative briefs with modular, bite-sized tasks."
        images={[sectionSlots]}
        reverse
        bullets={[
          "Structured micro-briefs: Every slot defines concise framing missions with explicit target durations (from 3 to 60 seconds, typically 5–8 seconds).",
          "Gemini-powered slot generation: Gemini automatically breaks down an overall prompt into structured JSON slots complete with shooting missions, locations, and durations.",
          "Visual framing guides: Reference slot illustrations can be generated automatically to give contributors immediate visual context on angle, mood, and composition.",
        ]}
        benefit="Field Weavers know exactly what to film without back-and-forth messaging or ambiguity."
      />

      <Section
        title={'Geolocated Radar & "Local Threads" Feed (< 25 km Radius)'}
        subtitle="The right shot, at the right location, captured by the right person already on-site."
        images={[section4]}
        bullets={[
          "Automated spatial detection: Location-aware intelligence matches open shooting slots with nearby creators based on proximity and camera capabilities.",
          "Proximity alerts: The Local Threads engine dispatches real-time alerts exclusively to Weavers located within a default 25 km radius of the Point of Interest (POI).",
          "Google Places anchoring: Exact coordinates and location tags guide Weavers directly to designated capture points.",
        ]}
        benefit="Eliminates expensive crew travel: hyper-local footage is sourced spontaneously from creators already present in the area."
        action={{ to: "/use-cases/geolocalisation", label: "Discover spatial matching →" }}
      />

      <Section
        title="Instant Network Building: Weaver & Co-Creator Onboarding"
        subtitle="Build, organize, and mobilize your collaborative production network in seconds."
        images={[section51, section52]}
        mediaVariant="duo"
        reverse
        bullets={[
          "Predictive search from 2 letters: Find collaborators immediately by typing first name, last name, or @username.",
          "1-click role toggles: Assign permissions instantly between on-the-ground contributors (Weaver) and delegated producers (Co-Creator) with editing and review rights.",
          "Integrated mobile chat: Direct 1-to-1 channels and group chats with emoji reactions (❤️) and read receipts keep teams coordinated without third-party messaging apps.",
        ]}
        benefit="Frictionless team formation allows brands and creators to spin up capture crews on demand."
      />

      <Section
        title="The Intelligent Notifications Hub: Clutter-Free Production Alerts"
        subtitle="Every shooting opportunity, AI insight, and review task in a unified control center."
        benefit="Creators and businesses stay fully organized without alert fatigue or chaotic notification noise"
      >
        <ul>
          <li>
            4 dedicated category hubs:
            <ul>
              <li>Local Threads: hyper-local filming alerts nearby.</li>
              <li>AI Suggests: proactive scenario ideas and storyboard proposals.</li>
              <li>Approvals: dedicated review queue for incoming video clips.</li>
              <li>Pulse: community activity metrics and incoming contribution counts.</li>
            </ul>
          </li>
          <li>Real-time WebSocket event delivery: Instant updates across mobile and web without manual screen refreshes.</li>
          <li>Single-tap cleanup: A universal "Mark all read" action clears badges and resets alerts in one tap.</li>
        </ul>
      </Section>

      <Section
        title="Frictionless Business Management: Multi-Tenant Workspace Isolation"
        subtitle="Enterprise-grade governance and secure asset separation built for brands and agencies."
        images={[section7]}
        bullets={[
          "Seamless organization switching: Switch between personal portfolios and corporate workspaces (e.g., Le Louvre, Century 21) with one tap.",
          "Strict multi-tenant boundaries: All media assets, drafts, notifications, and team chats are private by default and partitioned within tenant domains.",
          "Tenant administration: Dedicated business management roles (Tenant Admin) supervise contributors, oversee budgets, and track performance KPIs safely.",
        ]}
        benefit="Agencies and corporate teams collaborate with distributed networks without administrative overhead or data-leak risks."
      />

      <Section
        title={'The "As Creator" Cockpit & AI-Assisted Media Review'}
        subtitle="Review and approve community footage in seconds with automated AI screening."
        images={[section81, section82]}
        mediaVariant="duo"
        reverse
        bullets={[
          'Automated match evaluation: The Studio AI Reviewer analyzes submitted video frames against the slot brief to calculate a quantitative Fit Score (e.g., "Fit 86%") and quality assessment.',
          '"AI selected" tagging: The top-ranking take is automatically highlighted with a badge to accelerate creator decision-making.',
          "AI Content Guard pre-screening: Gemini Multimodal Vision and FFmpeg frame analysis screen unsafe or off-policy media before it enters the workflow.",
          'One-tap fallback: Creators can use the "Contribute to this slot" trigger to film or inject their own clip if a slot remains unfulfilled.',
        ]}
        benefit="Creators avoid sifting through hours of unorganized raw files, approving clips with single-tap precision."
        action={{ to: "/loom", label: "Deep dive the loom →" }}
      />

      <Section
        title="Zero-Friction Post-Production: Dual AI Automated Harmonization"
        subtitle="Your clips assemble, color-correct, and sync automatically—no desktop editing required."
        bullets={[
          "Connection AI (Instant consistency): Automatically balances disparate color profiles, aligns acoustic levels, and verifies spatiotemporal continuity across contributors.",
          "Generation AI (Dynamic packaging): Synthesizes multilingual voice-overs, aligns subtitles, and applies background music presets with automatic audio ducking.",
          "Living container (.weave): Enables individual clips to be hot-swapped dynamically without re-rendering or breaking live viewing links.",
        ]}
        benefit="Delivers professional, broadcast-grade video directly from mobile devices without expensive video editing suites."
        action={{ to: "/dual-ai", label: "Deep dive into the DUAL AI→" }}
      />

      <Section
        title="Automated Compensation & Trust: The Smart Escrow Engine"
        subtitle="Pay contributors fairly with zero invoicing hassle, no manual transfers, and no disputes."
        images={[section10]}
        bullets={[
          "100% upfront escrow locking: Total slot rewards are securely pre-funded into escrow before contributions are accepted.",
          "Instant cut-based disbursement: Bounties are irreversibly credited to the Weaver's In-App Wallet the moment their cut is approved into the final master (Woven state).",
          "Direct payout routing: Weavers monitor balances in their Wallet and withdraw earnings directly to PayPal once the minimum balance is reached.",
        ]}
        benefit="Absolute financial transparency: brands manage production budgets effortlessly, while contributors enjoy guaranteed, automated payouts."
        action={{ to: "/earning-model", label: "Check escrow & payout rules →" }}
      />

      <section className="coc-final">
        <h2>Deploy your decentralized video studio across your entire network.</h2>
        <p>Mobilize on-the-ground creators, subsidiaries, and field teams in clicks. Precision slot briefs, automated AI screening, and guaranteed smart escrow payouts: coordinate scalable productions with zero administrative drag.</p>
        <div className="coc-storeActions">
          <a
            className="coc-storeButton coc-storeButtonPrimary"
            href="https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr"
            target="_blank"
            rel="noreferrer"
          >
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button type="button" className="coc-storeButton coc-storeButtonSecondary" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
